import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiBase';
import { toast } from 'react-toastify';

/**
 * Unified API Client
 * Consolidated from apiClient.js and axiosInstance.js
 * Simple, clean, and efficient API management
 */

const DEFAULT_API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT, 10) || 300000;
const LARGE_UPLOAD_MAX_BYTES = 1024 * 1024 * 1024; // 1GB

const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  timeout: DEFAULT_API_TIMEOUT,
  maxContentLength: LARGE_UPLOAD_MAX_BYTES,
  maxBodyLength: LARGE_UPLOAD_MAX_BYTES,
  headers: {
    'Accept': 'application/json'
  },
  // Ensure CORS preflight is handled
  validateStatus: function (status) {
    return status >= 200 && status < 300; // default
  }
});

// Request interceptor - Add auth token and handle content types
apiClient.interceptors.request.use(
  (config) => {
    // Skip token for auth and public requests
    const publicEndpoints = [
      '/auth/login',
      '/auth/setup-2fa',
      '/auth/verify-setup',
      '/auth/verify-otp',
      '/auth/refresh-token',
      '/landing-page' // Add landing page to public endpoints
    ];
    
    const isPublicRequest = publicEndpoints.some(endpoint => config.url.includes(endpoint));
    
    if (!isPublicRequest) {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Large file uploads (SCORM ZIPs, avatars, etc.) should not time out on slow connections
    if (config.data instanceof FormData) {
      if (config.timeout === undefined || config.timeout === DEFAULT_API_TIMEOUT) {
        config.timeout = 0;
      }
      config.maxContentLength = LARGE_UPLOAD_MAX_BYTES;
      config.maxBodyLength = LARGE_UPLOAD_MAX_BYTES;
      // Let axios set the Content-Type for FormData (includes boundary)
      delete config.headers['Content-Type'];
    } else {
      // Set JSON content type for regular requests
      config.headers['Content-Type'] = 'application/json';
    }
    
    // Log only in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', config.method?.toUpperCase(), config.url);
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle token refresh and timeouts
apiClient.interceptors.response.use(
  (response) => {
    // Log only in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('API Response:', response.config?.method?.toUpperCase(), response.config?.url, response.status);
    }
    return response;
  },
  async (error) => {
    // Log errors (important for debugging)
    console.error('API Error:', error.config?.method?.toUpperCase(), error.config?.url, error.response?.status, error.message);
    
    const originalRequest = error.config;

    // Handle timeout errors with better messaging
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout:', error.config?.url);
      toast.error('Request timed out. Please check your connection and try again.', {
        position: "top-center",
        autoClose: 5000,
      });
      return Promise.reject(error);
    }

    // Handle network errors
    if (error.message === 'Network Error') {
      console.error('Network error:', error.config?.url);
      toast.error('Network error. Please check if the backend server is running.', {
        position: "top-center",
        autoClose: 5000,
      });
      return Promise.reject(error);
    }

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('🔐 401 Error detected, attempting token refresh...');
      console.log('🔐 Request URL:', originalRequest.url);
      console.log('🔐 Request method:', originalRequest.method);
      
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        console.log('🔐 Refresh token exists:', !!refreshToken);
        if (refreshToken) {
          const refreshResponse = await axios.post(`${getApiBaseUrl()}/auth/refresh-token`, {
            refresh_token: refreshToken
          }, {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (refreshResponse.data.success) {
            const { access_token } = refreshResponse.data.data;
            localStorage.setItem('accessToken', access_token);
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return apiClient(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        console.error('❌ Refresh error details:', {
          message: refreshError.message,
          status: refreshError.response?.status,
          data: refreshError.response?.data
        });
        
        const authKeys = ['accessToken', 'refreshToken', 'user', 'token', 'auth_token', 'jwt_token'];
        authKeys.forEach(key => {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        });

        const pathname = window.location.pathname || '';
        const onPublicAuthScreen = /^\/(login|2fa\/(setup|verify))(\/|$)/.test(pathname);
        const requestUrl = originalRequest?.url || '';
        const isAuthApiCall = /\/auth\/(login|setup-2fa|verify-setup|verify-otp|refresh-token)/.test(requestUrl);

        if (!onPublicAuthScreen && !isAuthApiCall && pathname !== '/login') {
          window.location.href = '/login';
          toast.error('Session expired. Please login again.');
        }
      }
    }

    return Promise.reject(error);
  }
);

/**
 * API Methods - Clean and simple
 */
const api = {
  // Authentication
  auth: {
    login: (email, password) => 
      apiClient.post('/auth/login', { email, password }),

    setup2fa: (tempToken, { regenerate = false } = {}) =>
      apiClient.post('/auth/setup-2fa', {
        temp_token: tempToken,
        ...(regenerate ? { regenerate: true } : {})
      }),

    verify2faSetup: ({ tempToken, otp }) =>
      apiClient.post('/auth/verify-setup', { temp_token: tempToken, otp }),

    verifyOtp: ({ tempToken, otp, backupCode }) =>
      apiClient.post('/auth/verify-otp', {
        temp_token: tempToken,
        ...(otp ? { otp } : {}),
        ...(backupCode ? { backup_code: backupCode } : {})
      }),
    
    logout: () => 
      apiClient.post('/auth/logout'),
    
    validateToken: () => 
      apiClient.get('/auth/validate-token'),
    
    refreshToken: (refreshToken) => 
      apiClient.post('/auth/refresh-token', { refresh_token: refreshToken }),

    forgotPassword: (email) =>
      apiClient.post('/auth/forgot-password', { email }),

    verifyResetToken: (token) =>
      apiClient.get(`/auth/verify-reset-token/${token}`),

    resetPassword: ({ token, password, confirmPassword }) =>
      apiClient.post('/auth/reset-password', {
        token,
        password,
        confirm_password: confirmPassword
      }),
    
    getProfile: () => 
      apiClient.get('/auth/profile'),

    // Update current user profile (supports avatar upload)
    updateProfile: (formData) => 
      apiClient.put('/auth/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),

    // Change current user's password
    changePassword: (currentPassword, newPassword, confirmPassword) =>
      apiClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      })
  },

  // Users
  users: {
    getAll: () => 
      apiClient.get('/users'),
    
    getById: (id) => 
      apiClient.get(`/users/${id}`),
    
    create: (userData) => 
      apiClient.post('/users', userData),
    
    update: (id, userData) => 
      apiClient.put(`/users/${id}`, userData),
    
    delete: (id) => 
      apiClient.delete(`/users/${id}`),
    
    restore: (id) => 
      apiClient.put(`/users/${id}/restore`),

    set2faEnabled: (id, enabled) =>
      apiClient.patch(`/users/${id}/2fa`, { enabled: Boolean(enabled) }),

    bulkValidate: (users) =>
      apiClient.post('/users/bulk/validate', { users }),

    bulkImport: (users) =>
      apiClient.post('/users/bulk/import', { users })
  },

  skills: {
    list: () => apiClient.get('/skills'),
    getMyAcquired: () => apiClient.get('/skills/acquired/mine'),
    create: (skillData) => apiClient.post('/skills', skillData),
    update: (id, skillData) => apiClient.put(`/skills/${id}`, skillData),
    remove: (id) => apiClient.delete(`/skills/${id}`)
  },

  roles: {
    list: () => apiClient.get('/roles'),
    create: (roleData) => apiClient.post('/roles', roleData),
    update: (id, roleData) => apiClient.put(`/roles/${id}`, roleData),
    remove: (id) => apiClient.delete(`/roles/${id}`)
  },

  accessControl: {
    getAll: () => apiClient.get('/access-control'),
    getByRole: (roleValue) => apiClient.get(`/access-control/role/${roleValue}`),
    upsert: (roleValue, menuIds) => apiClient.put(`/access-control/${roleValue}`, { menu_ids: menuIds })
  },

  instructor: {
    getDashboard: () => apiClient.get('/instructor/dashboard'),
    getCourses:   () => apiClient.get('/instructor/courses'),
    getUsers:     () => apiClient.get('/instructor/users')
  },

  groups: {
    list: () => apiClient.get('/groups'),
    create: (groupData) => apiClient.post('/groups', groupData),
    update: (id, groupData) => apiClient.put(`/groups/${id}`, groupData),
    remove: (id) => apiClient.delete(`/groups/${id}`),
    getUsers: (id, organizationId = null) =>
      apiClient.get(`/groups/${id}/users`, {
        params: organizationId ? { organizationId } : {}
      }),
    setUsers: (id, userIds, organizationId = null) =>
      apiClient.put(`/groups/${id}/users`, {
        user_ids: userIds,
        ...(organizationId ? { organization_id: organizationId } : {})
      }),
    getInstructors: (id, organizationId = null) =>
      apiClient.get(`/groups/${id}/instructors`, {
        params: organizationId ? { organizationId } : {}
      }),
    setInstructors: (id, userIds, organizationId = null) =>
      apiClient.put(`/groups/${id}/instructors`, {
        user_ids: userIds,
        ...(organizationId ? { organization_id: organizationId } : {})
      })
  },

  // Courses
  courses: {
    getAll: (adminView = false) => 
      apiClient.get(adminView ? '/courses/admin/all' : '/courses'),
    
    getById: (id) => 
      apiClient.get(`/courses/${id}`),
    
    create: (courseData) => 
      apiClient.post('/courses', courseData),
    
    update: (id, courseData) => 
      apiClient.put(`/courses/${id}`, courseData),
    
    delete: (id) => 
      apiClient.delete(`/courses/${id}`),
    
    restore: (id) => 
      apiClient.put(`/courses/${id}/restore`),

    getCourseInstructors: (courseId, orgId = null) =>
      apiClient.get(`/courses/${courseId}/instructors${orgId ? `?org_id=${orgId}` : ''}`),

    setCourseInstructors: (courseId, instructorIds) =>
      apiClient.put(`/courses/${courseId}/instructors`, { instructor_ids: instructorIds })
  },

  // Modules
  modules: {
    getByCourse: (courseId) => 
      apiClient.get(`/modules/course/${courseId}`),
    
    getById: (id) => 
      apiClient.get(`/modules/${id}`),
    
    create: (moduleData) => 
      apiClient.post('/modules', moduleData),
    
    update: (id, moduleData) => 
      apiClient.put(`/modules/${id}`, moduleData),
    
    delete: (id) => 
      apiClient.delete(`/modules/${id}`),
    
    restore: (id) => 
      apiClient.put(`/modules/${id}/restore`)
  },

  // Enrollments
  enrollments: {
    getAll: () => 
      apiClient.get('/enrollments'),
    
    getByUser: (userId) => 
      apiClient.get(`/enrollments/user/${userId}`),
    
    getByCourse: (courseId, options = {}) => {
      const params = {};
      if (options.orgId) params.org_id = options.orgId;
      if (options.includeDeleted) params.includeDeleted = 'true';
      return apiClient.get(`/enrollments/course/${courseId}`, { params });
    },
    
    create: (userId, courseId) => 
      apiClient.post('/enrollments', { userId, courseId }),
    
    delete: (id) => 
      apiClient.delete(`/enrollments/${id}`),
    
    restore: (id) => 
      apiClient.put(`/enrollments/${id}/restore`),

    enrollGroup: (courseId, groupId) =>
      apiClient.post(`/enrollments/course/${courseId}/enroll-group`, { group_id: groupId }),

    enrollOrgGroup: (courseId, groupId, orgId, schedule = {}) =>
      apiClient.post(`/enrollments/course/${courseId}/enroll-org-group`, {
        group_id: groupId,
        org_id: orgId,
        ...schedule
      }),

    getOrgGroupPreview: (courseId, groupId, orgId) =>
      apiClient.get(`/enrollments/course/${courseId}/group-preview/${groupId}`, { params: { org_id: orgId } }),

    getOrgAvailableLearners: (courseId, orgId) =>
      apiClient.get(`/enrollments/course/${courseId}/org-available-learners`, { params: { org_id: orgId } }),

    enrollOrgIndividuals: (courseId, userIds, orgId, schedule = {}) =>
      apiClient.post(`/enrollments/course/${courseId}/enroll-org-individuals`, {
        user_ids: userIds,
        org_id: orgId,
        ...schedule
      })
  },

  // Cart
  cart: {
    getItems: () =>
      apiClient.get('/cart'),

    getCount: () =>
      apiClient.get('/cart/count'),

    add: (courseId) =>
      apiClient.post('/cart', { courseId }),

    remove: (courseId) =>
      apiClient.delete(`/cart/${courseId}`)
  },

  // Payments
  payments: {
    initiate: (provider, voucherCode = null) =>
      apiClient.post('/payments/initiate', { provider, voucherCode }),

    complete: ({ paymentId, status, transactionId, details }) =>
      apiClient.post('/payments/complete', { paymentId, status, transactionId, details }),

    getStatus: (paymentId) =>
      apiClient.get(`/payments/${paymentId}/status`),

    listMy: () =>
      apiClient.get('/payments/my'),

    capturePaypal: ({ paymentId, orderId }) =>
      apiClient.post('/payments/paypal/capture', { paymentId, orderId }),

    captureStripe: ({ paymentId, sessionId }) =>
      apiClient.post('/payments/stripe/capture', { paymentId, sessionId }),

    cancelStripe: ({ paymentId }) =>
      apiClient.post('/payments/stripe/cancel', { paymentId })
  },

  // Vouchers
  vouchers: {
    list: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/vouchers${qs ? `?${qs}` : ''}`);
    },

    getById: (id) =>
      apiClient.get(`/vouchers/${id}`),

    create: (data) =>
      apiClient.post('/vouchers', data),

    update: (id, data) =>
      apiClient.put(`/vouchers/${id}`, data),

    remove: (id) =>
      apiClient.delete(`/vouchers/${id}`),

    restore: (id) =>
      apiClient.post(`/vouchers/${id}/restore`),

    getUsage: (id, params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/vouchers/${id}/usage${qs ? `?${qs}` : ''}`);
    }
  },

  // SCORM Tracking
  scormTracking: {
    // Get tracking data for a user, course, and module
    get: (userId, courseId, moduleId) => {
      if (!moduleId) {
        return apiClient.get(`/scorm-tracking/user/${userId}/course/${courseId}`);
      }
      return apiClient.get(`/scorm-tracking/${userId}/${courseId}/${moduleId}`);
    },
    
    // Get user tracking summary across all courses
    getUserSummary: (userId) => 
      apiClient.get(`/scorm-tracking/user/${userId}/summary`),
    
    // Get all tracking data for a user and course
    getCourseTracking: (userId, courseId) =>
      apiClient.get(`/scorm-tracking/user/${userId}/course/${courseId}`)
  },

  // Organizations
  organizations: {
    // Basic CRUD operations
    getAll: () => 
      apiClient.get('/orgs'),
    
    getById: (id) => 
      apiClient.get(`/orgs/${id}`),
    
    create: (orgData) => 
      apiClient.post('/orgs', orgData),
    
    update: (id, orgData) => 
      apiClient.put(`/orgs/${id}`, orgData),
    
    delete: (id) => 
      apiClient.delete(`/orgs/${id}`),
    
    restore: (id) => 
      apiClient.put(`/orgs/${id}/restore`),
    
    // Enable/disable 2FA for all users and admins of an organization
    set2faEnabled: (id, enabled) =>
      apiClient.patch(`/orgs/${id}/2fa`, { enabled: Boolean(enabled) }),
    
    // User management
    getUsers: (id, includeDeleted = false) => 
      apiClient.get(`/orgs/${id}/users`, {
        params: { includeDeleted: includeDeleted ? 'true' : 'false' }
      }),
    
    addUser: (id, userDataOrId) => 
      apiClient.post(`/orgs/${id}/users`, userDataOrId),
    
    updateUser: (orgId, userId, userData) => 
      apiClient.put(`/orgs/${orgId}/users/${userId}`, userData),
    
    removeUser: (orgId, userId) => 
      apiClient.delete(`/orgs/${orgId}/users/${userId}`),
    
    restoreUser: (orgId, userId) =>
      apiClient.put(`/orgs/${orgId}/users/${userId}/restore`),

    bulkValidateUsers: (orgId, users) =>
      apiClient.post(`/orgs/${orgId}/users/bulk/validate`, { users }),

    bulkImportUsers: (orgId, users) =>
      apiClient.post(`/orgs/${orgId}/users/bulk/import`, { users }),

    // Admin management
    getAdmins: (id, includeDeleted = false) => 
      apiClient.get(`/orgs/${id}/admins`, {
        params: { includeDeleted: includeDeleted ? 'true' : 'false' }
      }),
    
    addAdmin: (id, adminDataOrId) => 
      apiClient.post(`/orgs/${id}/admins`, adminDataOrId),
    
    updateAdmin: (orgId, adminId, adminData) => 
      apiClient.put(`/orgs/${orgId}/admins/${adminId}`, adminData),
    
    removeAdmin: (orgId, adminId) => 
      apiClient.delete(`/orgs/${orgId}/admins/${adminId}`),
    
    restoreAdmin: (orgId, adminId) => 
      apiClient.put(`/orgs/${orgId}/admins/${adminId}/restore`),
    
    // Get all organizations
    getAllOrganizations: () => 
      apiClient.get('/orgs'),
    
    // Course management
    getCourses: (id, includeDeleted = false) => 
      apiClient.get(`/orgs/${id}/courses`, {
        params: { includeDeleted: includeDeleted ? 'true' : 'false' }
      }),
    
    addCourse: (id, courseData) => 
      apiClient.post(`/orgs/${id}/courses`, courseData),
    
    updateCourse: (id, courseId, courseData) => 
      apiClient.put(`/orgs/${id}/courses/${courseId}`, courseData),
    
    removeCourse: (id, courseId) => 
      apiClient.delete(`/orgs/${id}/courses/${courseId}`),
    
    restoreCourse: (id, courseId) => 
      apiClient.put(`/orgs/${id}/courses/${courseId}/restore`)
  },

  // Licenses
  licenses: {
    // Super Admin - Get all license requests
    getAllRequests: (status = null, organizationId = null) => {
      const params = {};
      if (status && status !== 'all') params.status = status;
      if (organizationId) params.organization_id = organizationId;
      return apiClient.get('/licenses/requests', { params });
    },
    
    // Super Admin - Get all organization license usage
    getAllUsage: () => 
      apiClient.get('/licenses/usage'),
    
    // Super Admin - Approve license request
    approveRequest: (id, approvedLicenses, notes = null) => 
      apiClient.put(`/licenses/requests/${id}/approve`, { 
        approved_licenses: approvedLicenses, 
        notes 
      }),
    
    // Super Admin - Reject license request
    rejectRequest: (id, rejectionReason, notes = null) => 
      apiClient.put(`/licenses/requests/${id}/reject`, { 
        rejection_reason: rejectionReason, 
        notes 
      }),
    
    // Organization - Get organization license requests
    getOrganizationRequests: (orgId, status = null) => {
      const params = {};
      if (status && status !== 'all') params.status = status;
      return apiClient.get(`/licenses/organizations/${orgId}/requests`, { params });
    },
    
    // Organization - Create license request
    createRequest: (orgId, requestedLicenses, notes = null) => 
      apiClient.post(`/licenses/organizations/${orgId}/requests`, {
        organization_id: orgId,
        requested_licenses: requestedLicenses,
        notes
      }),
    
    // Organization - Get license usage
    getOrganizationUsage: (orgId) => 
      apiClient.get(`/licenses/organizations/${orgId}/usage`),
    
    // Check license availability
    checkAvailability: (orgId) => 
      apiClient.get(`/licenses/organizations/${orgId}/check`),
    
    // Consume license (for user creation)
    consumeLicense: (orgId) => 
      apiClient.post(`/licenses/organizations/${orgId}/consume`),
    
    // Release license (for user deletion)
    releaseLicense: (orgId) => 
      apiClient.post(`/licenses/organizations/${orgId}/release`)
  },

  // Support Tickets
  supportTickets: {
    // Get tickets (filtered based on user role and permissions)
    getAll: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/support-tickets?${queryParams}`);
    },
    
    // Get all tickets for super admin
    getAllForAdmin: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/support-tickets/admin/all?${queryParams}`);
    },
    
    // Get a specific ticket by ID
    getById: (id) => 
      apiClient.get(`/support-tickets/${id}`),
    
    // Create a new support ticket
    create: (ticketData) => 
      apiClient.post('/support-tickets', ticketData),
    
    // Update ticket status
    updateStatus: (id, statusData) => 
      apiClient.patch(`/support-tickets/${id}/status`, statusData),
    
    // Add a message to a ticket
    addMessage: (id, messageData) => 
      apiClient.post(`/support-tickets/${id}/messages`, messageData),
    
    // Upload attachment to a ticket
    uploadAttachment: (id, formData) => 
      apiClient.post(`/support-tickets/${id}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }),
    
    // Download attachment with proper authentication
    downloadAttachment: async (attachmentId) => {
      try {
        const response = await apiClient.get(`/support-tickets/attachments/${attachmentId}/download`, {
          responseType: 'blob'
        });
        
        // Get filename from Content-Disposition header
        const contentDisposition = response.headers['content-disposition'];
        let filename = `attachment_${attachmentId}`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        // Create blob URL and trigger download
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true };
      } catch (error) {
        console.error('Download failed:', error);
        throw error;
      }
    },
    
    // Get ticket statistics (for admin dashboard)
    getStats: () => 
      apiClient.get('/support-tickets/admin/stats')
  },

  // Dashboard
  dashboard: {
    // Get comprehensive dashboard statistics for Super Admin
    getStats: () => 
      apiClient.get('/dashboard/stats'),
    
    // Get quick dashboard statistics for fast loading
    getQuickStats: () => 
      apiClient.get('/dashboard/quick-stats'),

    // Get comprehensive dashboard statistics for Organization Admin
    getOrgStats: () =>
      apiClient.get('/dashboard/org-stats')
  },

  // Reports Analytics
  reports: {
    getSection: (scope, section, params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/reports/${scope}/${section}${qs ? `?${qs}` : ''}`);
    },

    getEnrollmentLearners: (scope, params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/reports/${scope}/course-enrollments/learners${qs ? `?${qs}` : ''}`);
    },

    getOrgLicenseRequests: (scope, organizationId) =>
      apiClient.get(`/reports/${scope}/organizations/${organizationId}/license-requests`),

    exportSection: (scope, section, params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/reports/${scope}/${section}/export.csv${qs ? `?${qs}` : ''}`, {
        responseType: 'blob'
      });
    }
  },

  // Forums
  forums: {
    // Get all forums with pagination and filtering
    getAll: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/forums?${queryParams}`);
    },
    
    // Get single forum by ID
    getById: (id) => 
      apiClient.get(`/forums/${id}`),
    
    // Create new forum
    create: (forumData) => {
      // If forumData is already a FormData object, use it directly
      if (forumData instanceof FormData) {
        console.log('🔍 API Client - Using FormData directly');
        // Log FormData contents for debugging
        for (let [key, value] of forumData.entries()) {
          console.log('🔍 FormData entry:', key, '=', value);
        }
        return apiClient.post('/forums', forumData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      // Otherwise, create FormData from object
      const formData = new FormData();
      formData.append('title', forumData.title);
      formData.append('description', forumData.description);
      if (forumData.org_id) formData.append('org_id', forumData.org_id);
      if (forumData.attachment) formData.append('attachment', forumData.attachment);
      
      return apiClient.post('/forums', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    
    // Update forum
    update: (id, forumData) => {
      // If forumData is already a FormData object, use it directly
      if (forumData instanceof FormData) {
        return apiClient.put(`/forums/${id}`, forumData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      // Otherwise, create FormData from object
      const formData = new FormData();
      if (forumData.title) formData.append('title', forumData.title);
      if (forumData.description) formData.append('description', forumData.description);
      if (forumData.org_id) formData.append('org_id', forumData.org_id);
      if (forumData.attachment) formData.append('attachment', forumData.attachment);
      
      return apiClient.put(`/forums/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    
          // Delete forum
          delete: (id) =>
            apiClient.delete(`/forums/${id}`),
          // Restore forum
          restore: (id) =>
            apiClient.post(`/forums/${id}/restore`),
    
    // Get forum comments
    getComments: (forumId, params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/forums/${forumId}/comments?${queryParams}`);
    },
    
    // Create forum comment
    createComment: (forumId, commentData) => {
      const formData = new FormData();
      formData.append('content', commentData.content);
      if (commentData.attachment) formData.append('attachment', commentData.attachment);
      
      return apiClient.post(`/forums/${forumId}/comments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    
    // Like/Unlike forum
    toggleLike: (forumId) => 
      apiClient.post(`/forums/${forumId}/like`),
    
    // Like/Unlike comment
    toggleCommentLike: (commentId) => 
      apiClient.post(`/forums/comments/${commentId}/like`),
    
    // Highlight comment as vulgar
    highlightComment: (commentId, reason) => 
      apiClient.post(`/forums/comments/${commentId}/highlight`, { reason: reason }),
    
    // Get highlighted comments for moderation
    getHighlightedComments: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/forums/moderation/highlighted?${queryParams}`);
    },
    
    // Moderate highlighted comment
    moderateHighlightedComment: (highlightId, action) => 
      apiClient.post(`/forums/moderation/highlighted/${highlightId}`, { action })
  },

  // VLT (Virtual-Led Training) API
  vlt: {
    // Get all VLTs
    getAll: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/vlt?${queryParams}`);
    },
    
    // Get single VLT by ID
    getById: (id) => 
      apiClient.get(`/vlt/${id}`),
    
    // Create new VLT
    create: (vltData) => {
      // If vltData is already a FormData object, use it directly
      if (vltData instanceof FormData) {
        return apiClient.post('/vlt', vltData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      // Otherwise, create FormData from object
      const formData = new FormData();
      formData.append('title', vltData.title);
      formData.append('description', vltData.description);
      formData.append('type', vltData.type);
      formData.append('url_or_path', vltData.url_or_path);
      if (vltData.course_id) formData.append('course_id', vltData.course_id);
      if (vltData.organization_id) formData.append('organization_id', vltData.organization_id);
      formData.append('status', vltData.status || 'active');
      if (vltData.thumbnail_image) formData.append('thumbnail_image', vltData.thumbnail_image);
      
      return apiClient.post('/vlt', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    
    // Update VLT
    update: (id, vltData) => {
      // If vltData is already a FormData object, use it directly
      if (vltData instanceof FormData) {
        return apiClient.put(`/vlt/${id}`, vltData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      // Otherwise, create FormData from object
      const formData = new FormData();
      if (vltData.title) formData.append('title', vltData.title);
      if (vltData.description) formData.append('description', vltData.description);
      if (vltData.type) formData.append('type', vltData.type);
      if (vltData.url_or_path) formData.append('url_or_path', vltData.url_or_path);
      if (vltData.course_id) formData.append('course_id', vltData.course_id);
      if (vltData.organization_id) formData.append('organization_id', vltData.organization_id);
      if (vltData.status) formData.append('status', vltData.status);
      if (vltData.thumbnail_image) formData.append('thumbnail_image', vltData.thumbnail_image);
      
      return apiClient.put(`/vlt/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    
    // Delete VLT
    delete: (id) => 
      apiClient.delete(`/vlt/${id}`),
    
    // Restore VLT
    restore: (id) => 
      apiClient.post(`/vlt/${id}/restore`),
    
    // Assign VLT
    assign: (assignmentData) => 
      apiClient.post('/vlt/assign', assignmentData),
    
    // Get user VLTs
    getUserVlts: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/vlt/user?${queryParams}`);
    },
    
    // Track VLT access
    trackAccess: (vltId, action, metadata = {}) => 
      apiClient.post(`/vlt/${vltId}/track`, { action, metadata }),
    
    // Get VLT reports
    getReports: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/vlt/reports?${queryParams}`);
    },

    // Get short-lived inline document preview URL (view-only)
    getDocumentPreviewToken: (id) =>
      apiClient.get(`/vlt/${id}/preview-token`),

    // Instructors for meeting link VLT (org-scoped or platform when no org)
    getInstructors: (organizationId = null) => {
      const query = organizationId ? `?organization_id=${organizationId}` : '';
      return apiClient.get(`/vlt/instructors/list${query}`);
    }
  },

  // Notifications
  notifications: {
    list: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const queryString = queryParams.toString();
      return apiClient.get(`/notifications${queryString ? `?${queryString}` : ''}`);
    },

    summary: () =>
      apiClient.get('/notifications/summary'),

    markAllRead: () =>
      apiClient.patch('/notifications/mark-all-read'),

    markRead: (notificationId) =>
      apiClient.patch(`/notifications/${notificationId}/read`),

    markUnread: (notificationId) =>
      apiClient.patch(`/notifications/${notificationId}/unread`)
  },

  // Certificate Management API
  certificates: {
    // Certificate Templates
    getTemplates: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/certificates/templates?${queryParams}`);
    },
    
    getTemplate: (id) => 
      apiClient.get(`/certificates/templates/${id}`),
    
    createTemplate: (templateData) => 
      apiClient.post('/certificates/templates', templateData),
    
    updateTemplate: (id, templateData) => 
      apiClient.put(`/certificates/templates/${id}`, templateData),
    
    deleteTemplate: (id) => 
      apiClient.delete(`/certificates/templates/${id}`),
    
    previewTemplate: (id, sampleData = {}) => 
      apiClient.post(`/certificates/templates/${id}/preview`, sampleData),
    
    // Course Certificate Assignments
    assignToCourse: (assignmentData) => 
      apiClient.post('/certificates/assign', assignmentData),
    
    updateAssignment: (id, assignmentData) => 
      apiClient.put(`/certificates/assign/${id}`, assignmentData),
    
    unassignFromCourse: (id) => 
      apiClient.delete(`/certificates/assign/${id}`),
    
    getCourseAssignment: (courseId) => 
      apiClient.get(`/certificates/course/${courseId}`),
    
    getAllAssignments: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/certificates/assignments?${queryParams}`);
    },
    
    // Certificate Generation
    generateCertificate: (generationData) => 
      apiClient.post('/certificates/generate', generationData),

    issueIfEligible: (userId, courseId) =>
      apiClient.post('/certificates/issue-if-eligible', { user_id: userId, course_id: courseId }),

    renderImage: (payload) =>
      apiClient.post('/certificates/render-image', payload, { responseType: 'blob' }),
    
    getLearnerCertificates: (userId, params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/certificates/learner/${userId}?${queryParams}`);
    },
    
    downloadCertificate: (id) => 
      apiClient.get(`/certificates/download/${id}`, { responseType: 'blob' })
  },

  // Surveys
  surveys: {
    // Survey Template Management
    getAll: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/surveys?${queryParams}`);
    },
    
    getById: (id) => 
      apiClient.get(`/surveys/${id}`),
    
    create: (formData) => 
      apiClient.post('/surveys', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
    
    update: (id, formData) => 
      apiClient.put(`/surveys/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }),
    
    delete: (id) => 
      apiClient.delete(`/surveys/${id}`),
    
    restore: (id) => 
      apiClient.post(`/surveys/${id}/restore`),
    
    // Survey Questions
    getQuestions: (surveyId) => 
      apiClient.get(`/surveys/${surveyId}/questions`),
    
    getQuestionById: (id) => 
      apiClient.get(`/surveys/questions/${id}`),
    
    createQuestion: (questionData) => 
      apiClient.post('/surveys/questions', questionData),
    
    bulkCreateQuestions: (questionData) => 
      apiClient.post('/surveys/questions/bulk', questionData),
    
    updateQuestion: (id, questionData) => 
      apiClient.put(`/surveys/questions/${id}`, questionData),
    
    deleteQuestion: (id) => 
      apiClient.delete(`/surveys/questions/${id}`),
    
    reorderQuestions: (surveyId, questionOrders) => 
      apiClient.post('/surveys/questions/reorder', { survey_id: surveyId, question_orders: questionOrders }),
    
    // Survey Responses (User)
    getAvailable: () => 
      apiClient.get('/surveys/available/list'),
    
    getForResponse: (surveyId) => 
      apiClient.get(`/surveys/${surveyId}/respond`),
    
    submitResponse: (responseData) => 
      apiClient.post('/surveys/responses/submit', responseData),
    
    getMyResponse: (surveyId) => 
      apiClient.get(`/surveys/responses/my/${surveyId}`),
    
    // Survey Responses (Admin)
    getResponses: (surveyId, params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/surveys/${surveyId}/responses?${queryParams}`);
    },
    
    getDetailedResponses: (surveyId) => 
      apiClient.get(`/surveys/${surveyId}/responses/detailed`),
    
    exportResponses: (surveyId) => 
      apiClient.get(`/surveys/${surveyId}/responses/export`, { responseType: 'blob' })
  },

  calendar: {
    getUserEvents: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/calendar/user/events?${queryParams}`);
    },
    getOrgEvents: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/calendar/org/events?${queryParams}`);
    },
    getInstructorEvents: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/calendar/instructor/events?${queryParams}`);
    },
    getAdminEvents: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      return apiClient.get(`/calendar/admin/events?${queryParams}`);
    }
  },

  assessments: {
    create: (payload) => apiClient.post('/assessments/admin/assessments/create', payload),
    getAll: () => apiClient.get('/assessments/admin/assessments'),
    getById: (id) => apiClient.get(`/assessments/admin/assessments/${id}`),
    update: (id, payload) => apiClient.put(`/assessments/admin/assessments/${id}`, payload),
    delete: (id) => apiClient.delete(`/assessments/admin/assessments/${id}`),

    addQuestion: (payload) => apiClient.post('/assessments/admin/questions/add', payload),
    updateQuestion: (id, payload) => apiClient.put(`/assessments/admin/questions/${id}`, payload),
    deleteQuestion: (id) => apiClient.delete(`/assessments/admin/questions/${id}`),

    assign: (payload) => apiClient.post('/assessments/admin/assignments/assign', payload),
    getAssignments: (assessmentId) =>
      apiClient.get('/assessments/admin/assignments', {
        params: assessmentId ? { assessment_id: assessmentId } : {}
      }),
    removeAssignment: (id) => apiClient.delete(`/assessments/admin/assignments/${id}`),
    getAdminResults: (assessmentId) => apiClient.get(`/assessments/admin/results/${assessmentId}`),
    getOrgAssessments: () => apiClient.get('/assessments/org/assessments'),

    getUserAssessments: () => apiClient.get('/assessments/user/assessments'),
    getUserAssessmentById: (id) => apiClient.get(`/assessments/user/assessment/${id}`),
    start: (assessmentId) => apiClient.post('/assessments/user/assessment/start', { assessment_id: assessmentId }),
    saveAnswer: (payload) => apiClient.post('/assessments/user/assessment/save-answer', payload),
    submit: (attemptId) => apiClient.post('/assessments/user/assessment/submit', { attempt_id: attemptId }),
    getUserResult: (assessmentId) => apiClient.get(`/assessments/user/results/${assessmentId}`)
  },

  learningJourneys: {
    // Admin
    getAll: (params = {}) => apiClient.get('/learning-journeys/admin', { params }),
    getById: (id) => apiClient.get(`/learning-journeys/admin/${id}`),
    create: (formData) => apiClient.post('/learning-journeys/admin', formData),
    update: (id, formData) => apiClient.put(`/learning-journeys/admin/${id}`, formData),
    delete: (id) => apiClient.delete(`/learning-journeys/admin/${id}`),
    addStep: (id, payload) => apiClient.post(`/learning-journeys/admin/${id}/steps`, payload),
    updateStep: (id, stepId, payload) => apiClient.put(`/learning-journeys/admin/${id}/steps/${stepId}`, payload),
    deleteStep: (id, stepId) => apiClient.delete(`/learning-journeys/admin/${id}/steps/${stepId}`),
    reorderSteps: (id, ordered_step_ids) =>
      apiClient.put(`/learning-journeys/admin/${id}/steps/reorder`, { ordered_step_ids }),
    getEnrollments: (id) => apiClient.get(`/learning-journeys/admin/${id}/enrollments`),
    enroll: (id, payload) => apiClient.post(`/learning-journeys/admin/${id}/enrollments`, payload),
    removeEnrollment: (id, enrollmentId) =>
      apiClient.delete(`/learning-journeys/admin/${id}/enrollments/${enrollmentId}`),
    getReport: (id, type = 'progress') =>
      apiClient.get(`/learning-journeys/admin/${id}/reports`, { params: { type } }),
    exportReport: (id, type = 'progress', format = 'csv') =>
      apiClient.get(`/learning-journeys/admin/${id}/reports/export`, {
        params: { type, format },
        responseType: format === 'pdf' ? 'json' : 'blob'
      }),
    sendReminder: (id, payload) => apiClient.post(`/learning-journeys/admin/${id}/reminders`, payload),

    // Learner
    getMyJourneys: () => apiClient.get('/learning-journeys/user'),
    getMyJourneyDetail: (id) => apiClient.get(`/learning-journeys/user/${id}`),
    startStep: (id, stepId) => apiClient.post(`/learning-journeys/user/${id}/steps/${stepId}/start`),
    completeStep: (id, stepId, payload = {}) =>
      apiClient.post(`/learning-journeys/user/${id}/steps/${stepId}/complete`, payload)
  },

  // Terms of Use (Site Management)
  terms: {
    // Admin: list all versions for current organization
    listVersions: () => apiClient.get('/terms/versions'),

    // Admin: create new draft version
    createVersion: (payload) => apiClient.post('/terms/versions', payload),

    // Admin: update existing draft version
    updateVersion: (id, payload) => apiClient.put(`/terms/versions/${id}`, payload),

    // Admin: publish version (makes it active and deactivates others)
    publishVersion: (id) => apiClient.post(`/terms/versions/${id}/publish`),

    // User: check if current user needs to accept latest terms
    getAcceptanceStatus: () => apiClient.get('/terms/acceptance-status'),

    // User: accept latest terms
    acceptLatest: (meta = {}) => apiClient.post('/terms/accept', meta),

    // User: get current published terms for their organization
    getCurrent: () => apiClient.get('/terms/current')
  },

  // Privacy Policy (Site Management)
  privacy: {
    // Admin: list all versions for current organization
    listVersions: () => apiClient.get('/privacy/versions'),

    // Admin: create new draft version
    createVersion: (payload) => apiClient.post('/privacy/versions', payload),

    // Admin: update existing draft version
    updateVersion: (id, payload) => apiClient.put(`/privacy/versions/${id}`, payload),

    // Admin: publish version (makes it active and deactivates others)
    publishVersion: (id) => apiClient.post(`/privacy/versions/${id}/publish`),

    // User: get current published privacy policy for their organization
    getCurrent: () => apiClient.get('/privacy/current')
  },

  // FAQs (Site Management)
  faqs: {
    // FAQ items (audience-based)
    list: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/faqs${qs ? `?${qs}` : ''}`);
    },

    // Public/user-facing FAQs
    listPublic: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/faqs/public${qs ? `?${qs}` : ''}`);
    },

    create: (payload) => apiClient.post('/faqs', payload),

    update: (id, payload) => apiClient.put(`/faqs/${id}`, payload),

    reorder: (items, organizationId = null) =>
      apiClient.put('/faqs/reorder', {
        organization_id: organizationId,
        items
      }),

    remove: (id, organizationId = null) =>
      apiClient.delete(`/faqs/${id}`, {
        data: { organization_id: organizationId }
      })
  },

  // Banners (Site Management)
  banners: {
    list: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/banners${qs ? `?${qs}` : ''}`);
    },

    create: (payload) => apiClient.post('/banners', payload),

    update: (id, payload) => apiClient.put(`/banners/${id}`, payload),

    reorder: (items, organizationId = null) =>
      apiClient.put('/banners/reorder', {
        organization_id: organizationId,
        items
      }),

    remove: (id, organizationId = null) =>
      apiClient.delete(`/banners/${id}`, {
        data: { organization_id: organizationId }
      })
  },

  // Features (Site Management)
  features: {
    list: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/features${qs ? `?${qs}` : ''}`);
    },

    create: (payload) => apiClient.post('/features', payload),

    update: (id, payload) => apiClient.put(`/features/${id}`, payload),

    reorder: (items, organizationId = null) =>
      apiClient.put('/features/reorder', {
        organization_id: organizationId,
        items
      }),

    remove: (id, organizationId = null) =>
      apiClient.delete(`/features/${id}`, {
        data: { organization_id: organizationId }
      })
  },

  // Awards (Site Management)
  awards: {
    list: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/awards${qs ? `?${qs}` : ''}`);
    },

    create: (payload) => apiClient.post('/awards', payload),

    update: (id, payload) => apiClient.put(`/awards/${id}`, payload),

    reorder: (items, organizationId = null) =>
      apiClient.put('/awards/reorder', {
        organization_id: organizationId,
        items
      }),

    remove: (id, organizationId = null) =>
      apiClient.delete(`/awards/${id}`, {
        data: { organization_id: organizationId }
      })
  },

  // Testimonials (Site Management)
  testimonials: {
    list: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/testimonials${qs ? `?${qs}` : ''}`);
    },

    create: (payload) => apiClient.post('/testimonials', payload),

    update: (id, payload) => apiClient.put(`/testimonials/${id}`, payload),

    reorder: (items, organizationId = null) =>
      apiClient.put('/testimonials/reorder', {
        organization_id: organizationId,
        items
      }),

    remove: (id, organizationId = null) =>
      apiClient.delete(`/testimonials/${id}`, {
        data: { organization_id: organizationId }
      })
  },

  // Landing Page (Site Management)
  landingPage: {
    getConfig: () => apiClient.get('/landing-page'),
    saveConfig: (payload) => apiClient.post('/landing-page', payload)
  },

  gamification: {
    getConfig: () => apiClient.get('/gamification/config'),
    updateActivity: (id, payload) => apiClient.put(`/gamification/activities/${id}`, payload),
    updateBadgeLevel: (id, payload) => apiClient.put(`/gamification/badge-levels/${id}`, payload),
    getLeaderboard: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/gamification/leaderboard${qs ? `?${qs}` : ''}`);
    },
    getMyProfile: () => apiClient.get('/gamification/my-profile'),
    acknowledgeLevelUp: () => apiClient.post('/gamification/acknowledge-level-up')
  },

  edugenie: {
    getConfig: () => apiClient.get('/edugenie/config'),
    createSession: () => apiClient.post('/edugenie/session'),
    listSessions: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/edugenie/sessions${qs ? `?${qs}` : ''}`);
    },
    getHistory: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.get(`/edugenie/history${qs ? `?${qs}` : ''}`);
    },
    clearHistory: (params = {}) => {
      const queryParams = new URLSearchParams(params);
      const qs = queryParams.toString();
      return apiClient.delete(`/edugenie/history${qs ? `?${qs}` : ''}`);
    },
    getConversation: (id) => apiClient.get(`/edugenie/conversations/${id}`),
    chat: (payload) => apiClient.post('/edugenie/chat', payload),
    submitFeedback: (payload) => apiClient.post('/edugenie/feedback', payload),
    getAdminStatus: () => apiClient.get('/edugenie/admin/status'),
    testIntent: (payload) => apiClient.post('/edugenie/admin/test-intent', payload),
    getAnalytics: () => apiClient.get('/edugenie/admin/analytics'),
    listSettings: () => apiClient.get('/edugenie/admin/settings'),
    upsertSetting: (payload) => apiClient.put('/edugenie/admin/settings', payload),
    listIntents: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return apiClient.get(`/edugenie/admin/intents${qs ? `?${qs}` : ''}`);
    },
    createIntent: (payload) => apiClient.post('/edugenie/admin/intents', payload),
    updateIntent: (id, payload) => apiClient.put(`/edugenie/admin/intents/${id}`, payload),
    deleteIntent: (id) => apiClient.delete(`/edugenie/admin/intents/${id}`),
    listKeywords: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return apiClient.get(`/edugenie/admin/keywords${qs ? `?${qs}` : ''}`);
    },
    createKeyword: (payload) => apiClient.post('/edugenie/admin/keywords', payload),
    updateKeyword: (id, payload) => apiClient.put(`/edugenie/admin/keywords/${id}`, payload),
    deleteKeyword: (id) => apiClient.delete(`/edugenie/admin/keywords/${id}`),
    listConversations: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return apiClient.get(`/edugenie/admin/conversations${qs ? `?${qs}` : ''}`);
    },
    listFeedback: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return apiClient.get(`/edugenie/admin/feedback${qs ? `?${qs}` : ''}`);
    },
    listUnknownQuestions: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return apiClient.get(`/edugenie/admin/unknown-questions${qs ? `?${qs}` : ''}`);
    },
    answerUnknownQuestion: (id, payload) =>
      apiClient.post(`/edugenie/admin/unknown-questions/${id}/answer`, payload),
    ignoreUnknownQuestion: (id) =>
      apiClient.post(`/edugenie/admin/unknown-questions/${id}/ignore`),
    listQuickActions: () => apiClient.get('/edugenie/admin/quick-actions'),
    updateQuickAction: (id, payload) => apiClient.put(`/edugenie/admin/quick-actions/${id}`, payload)
  }
};

// Direct axios-style methods for backward compatibility
api.get = (url, config) => apiClient.get(url, config);
api.post = (url, data, config) => apiClient.post(url, data, config);
api.put = (url, data, config) => apiClient.put(url, data, config);
api.delete = (url, config) => apiClient.delete(url, config);
api.patch = (url, data, config) => apiClient.patch(url, data, config);

// Export both the structured API and raw axios instance for flexibility
export { apiClient as axiosInstance }; // For backward compatibility with axiosInstance imports
export default api;
